import { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavBarHomePage, RegisterUser, Login, Loading, FooterHomePage } from '../components';
import { ContainerImg, TitleHowItsWork } from './style';
import ImgPartOne_ from '../../../assets/img/Part01.png';
import HowItWorks from '../../../assets/img/HowItWorks.png';
import { OPTION_MENU } from '../../../constants/menu';
import SectionOne from '../components/sectionOne';
import SectionRegister from '../components/SectionRegister';
import { getCurrentUser, isSessionValid } from '../../../service/CognitoAuthService';
import * as UserActions from '../../../core/users/action';
import * as HomeActions from '../../../core/home/action';
import * as VenueActions from '../../../core/venues/action';
import AboutSection from '../components/SectionAbout';
import { APP_ROUTES } from '../../../constants/app-routes';
import { GROUP_USER } from '../../../config/appGroupConfig';
import { clearSession, readAndClearExpiredFlag } from '../../../utils/auth/session';
import { onDisplayAlert } from '../../../utils/alerts/alerts';

export default function HomePageMain() {
  const dispatch = useDispatch();
  const { loginOpen, registerOpen, registerUserRole } = useSelector((store) => store.home);
  const [isLoading, setLoading] = useState(false);
  const [goAbout, setGoAbout] = useState(false);
  const navigate = useNavigate();

  const onSelectMenu = (value) => {
    // eslint-disable-next-line no-console
    console.log(value);
    switch (value) {
      case OPTION_MENU.LOGIN:
        dispatch(HomeActions.openLoginModal());
        return;
      case OPTION_MENU.JOIN_AS_VENUE:
        dispatch(HomeActions.openVenueRegisterModal());
        break;
      case OPTION_MENU.JOIN_AS_VENDOR:
        dispatch(HomeActions.openVendorRegisterModal());
        break;
      case OPTION_MENU.ABOUT_US:
        setGoAbout(true);
        break;
      case OPTION_MENU.HOME:
        setGoAbout(false);
        break;
      default:
        break;
    }
  };

  const openJoinVenue = () => {
    dispatch(HomeActions.openVenueRegisterModal());
  };

  const openJoinVendor = () => {
    dispatch(HomeActions.openVendorRegisterModal());
  };

  const onSuccess = (authResponse) => {
    try {
      console.log({ authResponse });
      const cognitoId = authResponse?.username;
      dispatch(UserActions.getUserByCognitoId(cognitoId, navigate));
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };
  const onSuccessRegister = (authResponse, roleUser) => {
    setLoading(false);
    dispatch(HomeActions.closeRegisterModal());
    if (authResponse?.username) {
      // Signed in: the user saga routes venues to Manage Venue (with its own
      // next-step notice) and vendors to the dashboard.
      if (roleUser !== GROUP_USER.VENUE) {
        onDisplayAlert({
          title: 'Welcome to fayVen',
          description:
            'Your vendor account is ready. Next: finish your profile, then browse markets and apply.',
          type: 'success',
        });
      }
      dispatch(UserActions.getUserByCognitoId(authResponse.username, navigate));
      return;
    }
    onDisplayAlert({
      title: 'Account created',
      description: 'Your fayVen account is ready. Sign in to continue.',
      type: 'success',
    });
    dispatch(HomeActions.openLoginModal());
  };

  const { user } = useSelector((store) => store.users);

  // After an expired-session teardown (axios interceptor cleared storage and
  // reloaded to '/'), show a one-time notice and open the login modal so the
  // returning user lands on an actionable screen instead of a silent redirect.
  useEffect(() => {
    if (readAndClearExpiredFlag()) {
      dispatch(HomeActions.openLoginModal());
      onDisplayAlert({
        title: 'Session expired',
        description: 'Your session has expired. Please sign in again.',
        type: 'info',
      });
    }
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const resumeSession = async () => {
      // Is there a stale Cognito record persisted in this browser? (Presence
      // only — not validity.) hasStoredCognitoUser lets us tell "expired saved
      // session" apart from "never logged in here".
      let hasStoredCognitoUser = false;
      try {
        getCurrentUser();
        hasStoredCognitoUser = true;
      } catch {
        hasStoredCognitoUser = false;
      }

      try {
        // Only resume if there is a VALIDATED session. Checking token presence
        // alone was the root cause of the expired-session flicker loop: a stale
        // token made this navigate to the dashboard, the route guard rejected
        // the expired session and bounced back here, and the token was never
        // cleared. Amplify's currentSession() silently refreshes an expired id
        // token when the refresh token is still valid, so a genuinely-still-
        // logged-in user is routed on normally; it only reports invalid / throws
        // once the refresh token has also expired.
        const sessionValid = await isSessionValid();
        if (cancelled) return;

        if (!sessionValid) {
          // Validity check returned false (vs throwing). If a stale record is
          // sitting here, tear it down so it stops lingering; otherwise just
          // stay on the landing page.
          if (hasStoredCognitoUser) clearSession({ expired: true });
          return;
        }

        if (!user) {
          const cognitoData = getCurrentUser();
          // The saga performs the dashboard navigation on success, so we do not
          // navigate here — avoids racing the guard before the user is loaded.
          if (cognitoData) {
            dispatch(UserActions.getUserByCognitoId(cognitoData.username, navigate));
          }
        } else {
          navigate(APP_ROUTES.DASHBOARD);
        }
      } catch (err) {
        if (cancelled) return;
        // The expired "saved session" case: Amplify threw because the refresh
        // token is also expired. This is exactly what the bug report describes.
        // Clear the stale session (which shows the "session expired" notice and
        // opens login on reload) instead of leaving a dead token behind.
        if (hasStoredCognitoUser) {
          clearSession({ expired: true });
        } else {
          dispatch(UserActions.resetUserState());
        }
      }
    };

    resumeSession();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, user]);

  return (
    <>
      {isLoading ? <Loading /> : null}
      <RegisterUser
        onLoading={(value) => {
          setLoading(value);
        }}
        onSuccess={onSuccessRegister}
        open={registerOpen}
        roleUser={registerUserRole}
        handleClose={() => dispatch(HomeActions.closeRegisterModal())}
      />
      <Login
        onLoading={(value) => {
          setLoading(value);
        }}
        onSuccess={onSuccess}
        open={loginOpen}
        handleClose={() => dispatch(HomeActions.closeLoginModal())}
      />
      <NavBarHomePage onSelectMenu={onSelectMenu} />
      {goAbout ? (
        <AboutSection />
      ) : (
        <div>
          {/* <SectionOne setGoAbout={setGoAbout} /> */}
          <Grid container>
            <Grid item xs={12} sx={{ mt: 12 }}>
              <TitleHowItsWork>HOW IT WORKS?</TitleHowItsWork>
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={8}>
              <ContainerImg src={HowItWorks} />
            </Grid>
          </Grid>
          <SectionRegister onClickVenue={openJoinVenue} onClickVendor={openJoinVendor} />
        </div>
      )}
      <FooterHomePage />
    </>
  );
}
